import React, { useEffect } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import PhoneInput from 'react-phone-number-input';
import Label from './Label';
import HelperText from './HelperText';
import colors from './constants/colors';

export const PhoneInputLayout = styled.div`
  position: relative;
  .PhoneInputCountry {
    display: none;
  }
  .PhoneInputInput {
    font-size: 16px;
    color: ${({ isDisabled }) => (isDisabled ? `${colors.lunarRays}` : `${colors.slateGrey}`)};

    fontWeight: 450;
    border-radius: 8px;
    box-shadow: none;
    outline: none;
    display: block;
    height: 60px;
    min-width: 100%;
    box-sizing: border-box;
    max-width: 100%;
    background: ${colors.ghostWhite};
    text-indent: 16px;
    border: ${({ errorMessage }) =>
      errorMessage ? `1.5px solid ${colors.opalFlame}` : `1.5px solid ${colors.littleDipper}`};
    &::placeholder {
      color: ${({ isDisabled }) => (isDisabled ? colors.littleDipper : colors.lunarRays)};
    }

    &:focus,
    &:hover {
      border: ${({ errorMessage, isDisabled }) => {
        if (isDisabled) return;
        if (errorMessage) {
          return `1.5px solid ${colors.opalFlame};`;
        }
        return `1.5px solid ${colors.tulleGrey}`;
      }}
  }
`;

const PhoneInputHookForm = ({
  defaultValue,
  label,
  ariaLabel,
  name,
  isDisabled,
  rules,
  helperText,
  ...props
}) => {
  const {
    control,
    formState: { errors },
    setValue,
  } = useFormContext();

  useEffect(() => {
    setValue(name, defaultValue);
  }, [name, setValue, defaultValue]);

  const errorMessage = errors?.[name]?.message;

  return (
    <PhoneInputLayout isDisabled={isDisabled} errorMessage={errorMessage}>
      <Label htmlFor={name} errorMessage={errorMessage}>
        {errorMessage || label}
      </Label>
      {helperText && <HelperText>{helperText}</HelperText>}
      <Controller
        control={control}
        name={name}
        rules={rules}
        render={({ field: { onChange, value, name, onBlur } }) => (
          <PhoneInput
            aria-label={ariaLabel}
            withCountryCallingCode
            onBlur={onBlur}
            disabled={isDisabled}
            onChange={onChange}
            value={value}
            name={name}
            {...props}
          />
        )}
      />
    </PhoneInputLayout>
  );
};

PhoneInputHookForm.propTypes = {
  ariaLabel: PropTypes.string.isRequired,
  defaultValue: PropTypes.string,
  name: PropTypes.string.isRequired,
  rules: PropTypes.any,
  label: PropTypes.string.isRequired,
  isDisabled: PropTypes.bool,
  helperText: PropTypes.string,
};

PhoneInputHookForm.defaultProps = {
  defaultValue: undefined,
  rules: undefined,
  isDisabled: false,
  helperText: undefined,
};

export default PhoneInputHookForm;
